import { AppConst, JigConst, MueConst, ReportConst, UtilsConst } from '@/constants'
import { StoreConst } from '@/store/constants'
import { apiRequest, apiRequestCustomConfig } from '@/store/modules/ApiRequestHelper'
import {
  BreakLockRequestPayload,
  ChangeJigOwnerRequest,
  ChangeJigOwnerResult,
  ChangeOwnerTenantRequest,
  ExportJigZipRequest,
  ExportJigZipResult,
  ImportJigZipFileRequest,
  ImportJigZipFileResult,
  JigMetadata,
  JigImportPresignResponse,
  JigImportStartRequest,
  JigPermissionCreateRequest,
  JigPermissionDeleteRequest,
  JigPermissionUpdateRequest,
  JigShareRequest,
  JigShareURLData,
  JigTransferJob,
  JigTransferResult,
  JigVisibilities,
  LoadMyJigsPayload,
  LoadTenantJigsPayload,
  LockRequestPayload,
  PollJigTransferJobRequest,
  RepairDeeplinkResult,
  UpdateJigMetadataRequest,
} from '@/store/modules/jig/types'
import { ReportRequest, UploadThumbnailRequest } from '@/store/types'
import { TenantHelpers } from '@/utils/tenant-helpers'
import { getJigSpaceServiceLockMock } from '@/utils/jig-service-lock-runtime'
import { parseJigTransferJobId } from '@/utils/jig-transfer-job-id'
import Axios, { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios'
import { JigListPaginatedResponse, Tenant } from '../app/types'

declare const consoleLog: any
declare const consoleError: any

const CancelToken = Axios.CancelToken
let myJigsListCall: any = null
let teamJigsListCall: any = null

const JIG_JOB_POLL_INTERVAL_MS = 1500
const JIG_JOB_POLL_TIMEOUT_MS = 11 * 60 * 1000
const JIG_JOB_POLL_REQUEST_TIMEOUT_MS = 30 * 1000
const JIG_JOB_MAX_TRANSIENT_ERRORS = 5
const JIG_IMPORT_UPLOAD_IDLE_TIMEOUT_MS = 60 * 1000
const JIG_IMPORT_UPLOAD_RESPONSE_TIMEOUT_MS = 3 * 60 * 1000
const JIG_TRANSFER_TRIGGER_TIMEOUT_MS = 60 * 1000
const JIG_IMPORT_RECONCILE_TIMEOUT_MS = 10 * 1000

function normalizeRequestError(error: any, fallback: string): { error: string; statusCode: number } {
  const statusCode = error?.response?.status || 0
  const responseMessage = error?.response?.data?.message

  if (responseMessage) return { error: responseMessage, statusCode }
  if (statusCode) return { error: `Something went wrong. Error code: ${statusCode}`, statusCode }
  if (error?.request) return { error: 'No response received from server', statusCode: 0 }
  return { error: error?.message || fallback, statusCode: 0 }
}

async function requestJigTransfer<T>(
  context: any,
  tenantId: number,
  request: any,
  actionDescription: string,
): Promise<JigTransferResult<T>> {
  let capturedErrorMessage = ''
  let capturedStatusCode = 0
  const result = await apiRequest(
    request,
    context,
    '',
    actionDescription,
    '',
    {
      [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
    },
    (_context: any, error: any) => {
      const normalized = normalizeRequestError(error, `Something went wrong ${actionDescription}.`)
      capturedErrorMessage = normalized.error
      capturedStatusCode = normalized.statusCode
    },
  )

  if (result === false) {
    return {
      success: false,
      error: capturedErrorMessage || `Something went wrong ${actionDescription}.`,
      statusCode: capturedStatusCode,
    }
  }

  return { success: true, data: result as unknown as T }
}

function jobIdFromResponse(job: JigTransferJob): number | null {
  return parseJigTransferJobId(job.jobId == null ? job.id : job.jobId)
}

function waitForJigTransferPoll(delayMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const finish = () => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', finish)
      resolve()
    }
    const timer = setTimeout(finish, delayMs)
    signal?.addEventListener('abort', finish, { once: true })
  })
}

function jigTransferJobsFromResponse(response: unknown): JigTransferJob[] {
  if (Array.isArray(response)) return response as JigTransferJob[]
  if (!response || typeof response !== 'object') return []

  const container = response as Record<string, unknown>
  const candidates = [container.jobs, container.values, container.data]
  return (candidates.find(Array.isArray) || []) as JigTransferJob[]
}

async function findImportJobByUploadKey(
  context: any,
  tenantId: number,
  uploadKey: string,
): Promise<JigTransferJob | undefined> {
  const result = await requestJigTransfer<unknown>(
    context,
    tenantId,
    {
      action: UtilsConst.HTTPAction.GET,
      url: AppConst.APIEndpoint.jobsV1Url,
      timeout: JIG_IMPORT_RECONCILE_TIMEOUT_MS,
    },
    'checking whether the Jig import started',
  )

  if (!result.success) return undefined
  return jigTransferJobsFromResponse(result.data).find((job) => job.jobMeta?.importUploadKey === uploadKey)
}

async function pollJigTransferJob(
  context: any,
  payload: PollJigTransferJobRequest,
): Promise<JigTransferResult<JigTransferJob>> {
  const pollIntervalMs = payload.pollIntervalMs ?? JIG_JOB_POLL_INTERVAL_MS
  const pollTimeoutMs = payload.pollTimeoutMs ?? JIG_JOB_POLL_TIMEOUT_MS
  const maxTransientErrors = payload.maxTransientErrors ?? JIG_JOB_MAX_TRANSIENT_ERRORS
  const startedAt = Date.now()
  let transientErrors = 0
  const remainingTimeMs = () => Math.max(0, pollTimeoutMs - (Date.now() - startedAt))

  while (remainingTimeMs() > 0) {
    if (payload.signal?.aborted) {
      return { success: false, error: 'The Jig transfer was cancelled.', stage: 'poll', failureKind: 'cancelled', jobId: payload.jobId }
    }

    const result = await requestJigTransfer<JigTransferJob>(
      context,
      payload.tenantId,
      {
        action: UtilsConst.HTTPAction.GET,
        url: `${AppConst.APIEndpoint.jobsV1Url}/${payload.jobId}`,
        // Keep one black-holed GET from consuming the full polling budget. The
        // overall deadline still bounds the final request when less time remains.
        timeout: Math.max(1, Math.min(JIG_JOB_POLL_REQUEST_TIMEOUT_MS, remainingTimeMs())),
        signal: payload.signal,
      },
      `polling Jig transfer job ${payload.jobId}`,
    )

    if (!result.success || !result.data) {
      if (payload.signal?.aborted) {
        return { success: false, error: 'The Jig transfer was cancelled.', stage: 'poll', failureKind: 'cancelled', jobId: payload.jobId }
      }
      if (remainingTimeMs() <= 0) break
      const retryable = !result.statusCode || result.statusCode === 404 || result.statusCode === 429 || result.statusCode >= 500
      if (retryable) {
        transientErrors++
        if (transientErrors > maxTransientErrors) {
          return {
            success: false,
            error: 'The Jig transfer started, but its current status could not be confirmed.',
            statusCode: result.statusCode,
            stage: 'poll',
            failureKind: 'unknown',
            jobId: payload.jobId,
          }
        }
        const retryDelayMs = pollIntervalMs * 2 ** Math.min(transientErrors - 1, 3)
        await waitForJigTransferPoll(Math.min(retryDelayMs, remainingTimeMs()), payload.signal)
        continue
      }
      // Retrying an auth failure inside this loop cannot help, but the job was already
      // accepted and may still be running or finished. Only the job response itself
      // (below) can confirm a terminal failure, so keep the outcome unknown and hold on
      // to the job ID — dropping it lets a retry duplicate an import or overlap an export.
      return {
        success: false,
        error: result.error || 'The Jig transfer job could not be read.',
        statusCode: result.statusCode,
        stage: 'poll',
        failureKind: 'unknown',
        jobId: payload.jobId,
      }
    }

    // JigAPI's async Jig import/export handlers explicitly use the
    // api_processing -> finished/failed lifecycle. Library pipeline jobs share
    // /jobs/v1 but use a separate status vocabulary.
    const jobStatus = String(result.data.status)
    const statusBehavior = JigConst.getJigTransferJobBehavior(jobStatus)
    if (statusBehavior === JigConst.JigTransferJobBehavior.Unknown) {
      transientErrors++
      if (transientErrors > maxTransientErrors) {
        return {
          success: false,
          error: `The Jig transfer returned an unknown job status: ${jobStatus}.`,
          stage: 'job',
          failureKind: 'unknown',
          jobId: payload.jobId,
        }
      }
      const retryDelayMs = pollIntervalMs * 2 ** Math.min(transientErrors - 1, 3)
      await waitForJigTransferPoll(Math.min(retryDelayMs, remainingTimeMs()), payload.signal)
      continue
    }
    transientErrors = 0
    if (statusBehavior === JigConst.JigTransferJobBehavior.Failure) {
      return {
        success: false,
        error: result.data.errorMsg || 'The Jig transfer job failed.',
        stage: 'job',
        failureKind: 'confirmed',
        jobId: payload.jobId,
      }
    }
    if (statusBehavior === JigConst.JigTransferJobBehavior.Success) return result
    await waitForJigTransferPoll(Math.min(pollIntervalMs, remainingTimeMs()), payload.signal)
  }

  return {
    success: false,
    error: 'The Jig transfer timed out while waiting for processing to finish.',
    stage: 'poll',
    failureKind: 'unknown',
    jobId: payload.jobId,
  }
}

function craftJigListQueryParamsFilters(urlParams: Record<string, any>, loadConfig: LoadTenantJigsPayload, tenantId: number) {
  for (const key in loadConfig) {
    if (key === 'filters') {
      urlParams.filter = []
      for (const filterKey in loadConfig[key]) {
        const item = loadConfig[key][filterKey]
        // `action` value should be `eq` or `not_eq`. If empty skip this filter
        if (item.action == null || item.action === '') {
          continue
        }

        if (filterKey === JigConst.JigListQueryFilters.tenantOwner) {
          item.value = tenantId
        }

        urlParams.filter.push(`${filterKey},${item.action},${item.value}`)
      }
    } else if (key === 'sort') {
      urlParams.sort = `${loadConfig[key].by},${loadConfig[key].value}`
    } else {
      urlParams[key] = loadConfig[key as keyof LoadTenantJigsPayload]
    }
  }
}

export default {
  executeStatisticReport(context: any, reportRequest: ReportRequest): Promise<boolean> {
    let urlParams = {}
    let myMutation = ''
    const tenantId = this.getMyTenantID(context.rootState.app)

    switch (reportRequest.Name) {
      case ReportConst.Report.TenantDashboard:
        myMutation = 'updateJigViewGraph'
        urlParams = {
          name: reportRequest.Name,
          jig_hash_id: reportRequest.JigHashId,
          start_date: reportRequest.StartDate,
          end_date: reportRequest.EndDate,
          comparePreviousPeriod: reportRequest.ComparePreviousPeriod,
        }
        break
      case ReportConst.Report.JigViewActivity:
        myMutation = 'updateJigViewGraph'
        urlParams = {
          name: reportRequest.Name,
          jig_hash_id: reportRequest.JigHashId,
          start_date: reportRequest.StartDate,
          end_date: reportRequest.EndDate,
          comparePreviousPeriod: reportRequest.ComparePreviousPeriod,
        }
        break
      case ReportConst.Report.MostPopularJig:
        myMutation = 'updateMostPopularJigGraph'
        urlParams = {
          name: reportRequest.Name,
          top: reportRequest.Limit,
          reportDays: reportRequest.LookBackDays,
          comparePreviousPeriod: reportRequest.ComparePreviousPeriod,
        }
        break
      case ReportConst.Report.AppActivities:
        myMutation = 'updateAppActivitiesGraph'
        urlParams = {
          name: reportRequest.Name,
          // some other query params...
        }
        break
      default:
        myMutation = 'mutation' + reportRequest.Name
    }

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: `${AppConst.APIEndpoint.statisticsUrl}`,
        params: urlParams,
      },
      context,
      myMutation,
      `executing report ${reportRequest.Name} using mutation ${myMutation}`,
      StoreConst.StoreHelper.isStatisticsLoadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  async loadUserOwnedJigs(context: any, payload: LoadMyJigsPayload): Promise<boolean> {
    return await context.dispatch('loadJigCount', {
      ...payload,
      mutationName: payload.mutationName || '',
      actionDetails: payload.actionDetails || `load user`,
    })
  },

  async loadMyJigs(context: any, payload: LoadMyJigsPayload): Promise<boolean> {
    if (!payload.requestUserId) {
      return Promise.reject('Need requestUserId to retreive myJigs.')
    }

    myJigsListCall = payload.sharedWithUser ? CancelToken.source() : undefined

    // As My Jigs page and Team Jigs page are using the same view template,
    // if route changes while GET request is still ongoing plus the Jigs list is reasonable long,
    // it will overwrite the result of new GET request call on new route.
    // Or there will be a noticeable time delay before new list appears.
    // So we need to cancel any ongoing call to avoid situation like Team Jigs are showing on My Jigs page
    // or My Jigs showing on Team Jigs page.
    if (teamJigsListCall) {
      teamJigsListCall.cancel('Aborting request to get Team Jigs before get My Jigs request.')
    }

    const tenantId = this.getMyTenantID(context.rootState.app)
    const requestUrl = `${AppConst.APIEndpoint.tenantsV1Url}/${tenantId}${payload.listingUrl || AppConst.APIEndpoint.JigsUrlV3}`
    const mutationName = payload.mutationName != null ? payload.mutationName : 'updateMyJigs'
    const actionDetails = payload.actionDetails != null ? payload.actionDetails : 'my jigs'
    const loadConfig: LoadTenantJigsPayload = payload.loadConfig

    if (!loadConfig.filters[JigConst.JigListQueryFilters.ownerUserId].value) {
      loadConfig.filters[JigConst.JigListQueryFilters.ownerUserId].value = payload.requestUserId
    }

    const urlParams: Record<string, any> = {}
    craftJigListQueryParamsFilters(urlParams, payload.loadConfig, tenantId)

    if (payload.checkOtherUser) {
      urlParams.filter.push(`${JigConst.JigListQueryFilters.tenantOwner},eq,${tenantId}`)
    }

    const result = await apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: requestUrl,
        params: urlParams,
        paramsSerializer: {
          indexes: null, // (1) indexes: null (leads to no brackets), (2) (default) indexes: false (leads to empty brackets), (3) indexes: true (leads to brackets with indexes).
        },
        cancelToken: myJigsListCall ? myJigsListCall.token : undefined,
      },
      context,
      '',
      `getting ${actionDetails}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )

    if (result) {
      if (mutationName) {
        context.commit(mutationName, result)
        context.commit('updateMyJigCount', result)
      }
    }

    myJigsListCall = undefined

    return result
  },

  async loadTeamJigs(context: any, payload: LoadTenantJigsPayload): Promise<boolean> {
    teamJigsListCall = CancelToken.source()

    if (myJigsListCall) {
      myJigsListCall.cancel('Aborting request to get My Jigs before get Team Jigs request.')
    }

    const tenantId = this.getMyTenantID(context.rootState.app)
    const requestUrl = `${AppConst.APIEndpoint.tenantsV1Url}/${tenantId}${AppConst.APIEndpoint.JigsUrlV3}`
    const urlParams: Record<string, any> = {}

    craftJigListQueryParamsFilters(urlParams, payload, tenantId)

    const result = await apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: requestUrl,
        params: urlParams,
        paramsSerializer: {
          indexes: null, // by default: false
        },
        cancelToken: teamJigsListCall.token,
      },
      context,
      '',
      `getting team jigs`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )

    if (result) {
      context.commit('updateTeamJigs', result)

      if (urlParams.filter.length === 0) {
        // Shared with team Jigs
        context.commit('updateTenantSharedJigCount', result)
      } else if (urlParams.filter.length === 1) {
        // All Jigs in tenant
        context.commit('updateTenantTotalJigCount', result)
      } else {
        // Private Jigs in tenant
        context.commit('updateTenantPrivateJigCount', result)
      }
    }

    teamJigsListCall = null

    return result
  },

  async loadJigCount(context: any, payload: any = {}): Promise<boolean | JigListPaginatedResponse> {
    if (context.rootState.app.myTenantIndex === TenantHelpers.InvalidTenantIndex) {
      return Promise.reject('Invalid Tenant Index')
    }
    const myTenant = context.rootState.app.myTenants[context.rootState.app.myTenantIndex] as Tenant
    if (myTenant === null || myTenant.ID <= 0) {
      return Promise.reject('Invalid Tenant ID')
    }

    let requestParams: any = {
      op: 'count',
    }

    const targetUid = payload.requestUserId || this.authService().uid

    if (payload.filter == null) {
      requestParams.filter = [`${JigConst.JigListQueryFilters.tenantOwner},eq,${myTenant.ID}`]

      if (payload.checkOtherUser || myTenant.Subscription.MaxJigsCountPer === 'user') {
        requestParams.filter.push(`${JigConst.JigListQueryFilters.ownerUserId},eq,${targetUid}`)
      }

      requestParams.filter.push(`${JigConst.JigListQueryFilters.sharedWithUser},eq,${payload.sharedWithUser || false}`)
    } else {
      requestParams = {
        ...requestParams,
        filter: [...payload.filter],
      }

      if (payload.checkOtherUser || myTenant.Subscription.MaxJigsCountPer === 'user') {
        // If `owner_user_id` filter is not provided, append it
        if (requestParams.filter.find((f: string) => f.indexOf(`${JigConst.JigListQueryFilters.ownerUserId},eq`) >= 0) == null) {
          requestParams.filter.push(`${JigConst.JigListQueryFilters.ownerUserId},eq,${targetUid}`)
        }

        if (!payload.checkOtherUser) {
          const toRemoveIndex = requestParams.filter.findIndex(
            (f: string) => f.indexOf(`${JigConst.JigListQueryFilters.tenantOwner},eq`) >= 0
          )

          if (toRemoveIndex !== UtilsConst.invalidIndex) {
            requestParams.filter.splice(toRemoveIndex, 1)
          }
        }
      } else if (myTenant.Subscription.MaxJigsCountPer !== 'user') {
        const toRemoveIndex = requestParams.filter.findIndex(
          (f: string) => f.indexOf(`${JigConst.JigListQueryFilters.sharedWithUser},eq`) >= 0
        )

        if (toRemoveIndex !== UtilsConst.invalidIndex) {
          requestParams.filter.splice(toRemoveIndex, 1)
        }
      }

      delete requestParams.page
      delete requestParams.limit
      delete requestParams.sort
    }

    const requestUrl: string =
      payload.requestUrl || `${AppConst.APIEndpoint.tenantsV1Url}/${myTenant.ID}${AppConst.APIEndpoint.JigsUrlV3}`
    const mutationName = payload.mutationName != null ? payload.mutationName : 'updateTenantTotalJigCount'
    const actionName = payload.actionName || 'total Jig count'

    const result: boolean | JigListPaginatedResponse = await apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: requestUrl,
        params: requestParams,
        paramsSerializer: {
          indexes: null, // by default: false
        },
      },
      context,
      mutationName,
      `loading tenant ${actionName}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: myTenant.ID,
      }
    )

    if (typeof result !== 'boolean') {
      return {
        ...(result as JigListPaginatedResponse),
        uid: targetUid,
      }
    } else {
      return result
    }
  },

  loadJigMetadata(context: any, payload: any): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: `${AppConst.APIEndpoint.jigsV2Url}/${payload.jigId}/${AppConst.APIEndpoint.jigMetadataV1}`,
      },
      context,
      'updateJigMetadata',
      `getting jig ${payload.jigId}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  pollJigTransferJob(context: any, payload: PollJigTransferJobRequest): Promise<JigTransferResult<JigTransferJob>> {
    const jobId = parseJigTransferJobId(payload?.jobId)
    if (!payload?.tenantId || payload.tenantId === TenantHelpers.InvalidTenantID || jobId === null) {
      return Promise.resolve({ success: false, error: 'Invalid Jig transfer job request', stage: 'poll' })
    }
    return pollJigTransferJob(context, { ...payload, jobId })
  },

  async importJigZipFile(
    context: any,
    payload: ImportJigZipFileRequest,
  ): Promise<JigTransferResult<ImportJigZipFileResult>> {
    if (!payload?.tenantId || payload.tenantId === TenantHelpers.InvalidTenantID) {
      return { success: false, error: 'Invalid tenant', stage: 'presign' }
    }
    if (!payload.file) {
      return { success: false, error: 'Select a Jig ZIP file to import.', stage: 'presign' }
    }
    if (payload.file.size === 0) {
      return { success: false, error: 'The selected Jig ZIP file is empty.', stage: 'presign', failureKind: 'confirmed' }
    }
    if (payload.file.size > JigConst.JigImportMaxFileSizeBytes) {
      return { success: false, error: 'Jig ZIP files must be 500 MB or smaller.', statusCode: 413, stage: 'presign' }
    }

    payload.onStageChange?.('preparing')
    const presign = await requestJigTransfer<JigImportPresignResponse>(
      context,
      payload.tenantId,
      {
        action: UtilsConst.HTTPAction.POST,
        url: AppConst.APIEndpoint.jigImportPresignV1Url,
        timeout: JIG_TRANSFER_TRIGGER_TIMEOUT_MS,
      },
      'preparing the Jig ZIP upload',
    )
    if (!presign.success || !presign.data) {
      return { success: false, error: presign.error, statusCode: presign.statusCode, stage: 'presign' }
    }
    if (!presign.data.uploadUrl || !presign.data.uploadKey || !presign.data.contentType) {
      return { success: false, error: 'The upload preparation response was incomplete.', stage: 'presign' }
    }

    const uploadController = new AbortController()
    let uploadIdleTimer: ReturnType<typeof setTimeout> | null = null
    let uploadResponseTimer: ReturnType<typeof setTimeout> | null = null
    let uploadTimeoutReason: 'idle' | 'response' | 'unknown-total' | null = null
    let uploadedBytes = 0
    const resetUploadIdleTimer = () => {
      if (uploadResponseTimer !== null) {
        clearTimeout(uploadResponseTimer)
        uploadResponseTimer = null
      }
      if (uploadIdleTimer !== null) clearTimeout(uploadIdleTimer)
      uploadIdleTimer = setTimeout(() => {
        uploadTimeoutReason = 'idle'
        uploadController.abort()
      }, JIG_IMPORT_UPLOAD_IDLE_TIMEOUT_MS)
    }
    const armUploadResponseTimer = (timeoutReason: 'response' | 'unknown-total') => {
      if (uploadIdleTimer !== null) {
        clearTimeout(uploadIdleTimer)
        uploadIdleTimer = null
      }
      if (uploadResponseTimer !== null) clearTimeout(uploadResponseTimer)
      uploadResponseTimer = setTimeout(() => {
        uploadTimeoutReason = timeoutReason
        uploadController.abort()
      }, JIG_IMPORT_UPLOAD_RESPONSE_TIMEOUT_MS)
    }

    try {
      payload.onStageChange?.('uploading')
      resetUploadIdleTimer()
      await Axios.put(presign.data.uploadUrl, payload.file, {
        signal: uploadController.signal,
        headers: {
          'Content-Type': presign.data.contentType,
        },
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (event.loaded > uploadedBytes) {
            uploadedBytes = event.loaded
            if (!event.total) armUploadResponseTimer('unknown-total')
            else if (event.loaded >= event.total) armUploadResponseTimer('response')
            else resetUploadIdleTimer()
          }
          if (!payload.onUploadProgress || !event.total) return
          payload.onUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)))
        },
      })
    } catch (error: any) {
      if (uploadController.signal.aborted) {
        return {
          success: false,
          error: uploadTimeoutReason === 'response'
            ? 'The Jig ZIP upload finished sending, but storage did not respond within 3 minutes.'
            : uploadTimeoutReason === 'unknown-total'
              ? 'The Jig ZIP upload stopped because no progress or storage response was received for 3 minutes.'
              : 'The Jig ZIP upload stopped because no progress was received for 60 seconds.',
          stage: 'upload',
          failureKind: 'confirmed',
        }
      }
      const normalized = normalizeRequestError(error, 'The Jig ZIP upload failed.')
      return {
        success: false,
        error:
          normalized.statusCode === 403
            ? 'The storage upload was rejected. The upload URL may have expired or its Content-Type did not match.'
            : normalized.error,
        statusCode: normalized.statusCode,
        stage: 'upload',
      }
    } finally {
      if (uploadIdleTimer !== null) clearTimeout(uploadIdleTimer)
      if (uploadResponseTimer !== null) clearTimeout(uploadResponseTimer)
    }

    const startPayload: JigImportStartRequest = {
      uploadKey: presign.data.uploadKey,
      filename: payload.file.name,
      importNew: true,
      unityAppZip: false,
    }
    payload.onStageChange?.('processing')
    const start = await requestJigTransfer<JigTransferJob>(
      context,
      payload.tenantId,
      {
        action: UtilsConst.HTTPAction.POST,
        url: AppConst.APIEndpoint.jigImportV1Url,
        body: startPayload,
        timeout: JIG_TRANSFER_TRIGGER_TIMEOUT_MS,
      },
      'starting the Jig import',
    )
    let jobId = start.success && start.data ? jobIdFromResponse(start.data) : null
    const shouldReconcileStart = start.success || !start.statusCode || start.statusCode === 408 || start.statusCode >= 500
    if (jobId === null && shouldReconcileStart) {
      const recoveredJob = await findImportJobByUploadKey(context, payload.tenantId, presign.data.uploadKey)
      if (recoveredJob) jobId = jobIdFromResponse(recoveredJob)
    }
    if (jobId === null) {
      const isDefinitiveClientFailure = !start.success && Boolean(start.statusCode) && start.statusCode! >= 400 && start.statusCode! < 500 && start.statusCode !== 408
      if (isDefinitiveClientFailure) {
        return {
          success: false,
          error: start.error,
          statusCode: start.statusCode,
          stage: 'start',
          failureKind: 'confirmed',
          uploadKey: presign.data.uploadKey,
        }
      }
      return {
        success: false,
        error: 'The import request may have started, but its status could not be confirmed. Check the Jig listing before importing this ZIP again.',
        statusCode: start.statusCode,
        stage: 'start',
        failureKind: 'unknown',
        uploadKey: presign.data.uploadKey,
      }
    }

    const job = await pollJigTransferJob(context, {
      tenantId: payload.tenantId,
      jobId,
      pollIntervalMs: payload.pollIntervalMs,
      pollTimeoutMs: payload.pollTimeoutMs,
    })
    if (!job.success || !job.data) {
      return {
        success: false,
        error: job.error,
        statusCode: job.statusCode,
        stage: job.stage,
        failureKind: job.failureKind,
        jobId,
        uploadKey: presign.data.uploadKey,
      }
    }

    const jigHashId = job.data.jobMeta?.importCreatedJigHashId
    if (!jigHashId) {
      return {
        success: false,
        error: 'The import completed, but its Jig identifier could not be confirmed. Check the listing before importing this ZIP again.',
        stage: 'job',
        failureKind: 'unknown',
        jobId,
        uploadKey: presign.data.uploadKey,
      }
    }

    return {
      success: true,
      data: {
        jigId: job.data.jobMeta?.importCreatedJigId,
        jigHashId,
      },
    }
  },

  async exportJigZip(context: any, payload: ExportJigZipRequest): Promise<JigTransferResult<ExportJigZipResult>> {
    if (!payload?.tenantId || payload.tenantId === TenantHelpers.InvalidTenantID) {
      return { success: false, error: 'Invalid tenant', stage: 'start' }
    }
    if (!payload.hashId) return { success: false, error: 'A Jig identifier is required for export.', stage: 'start' }

    let jobId = parseJigTransferJobId(payload.resumeJobId)
    if (payload.resumeJobId !== undefined && jobId === null) {
      return {
        success: false,
        error: 'The existing Jig export job identifier is invalid. Its status could not be confirmed.',
        stage: 'poll',
        failureKind: 'unknown',
      }
    }
    if (jobId === null) {
      const start = await requestJigTransfer<JigTransferJob>(
        context,
        payload.tenantId,
        {
          action: UtilsConst.HTTPAction.POST,
          url: `${AppConst.APIEndpoint.jigsV2Url}/${payload.hashId}/export/v1`,
          // Do not abort the start response: the backend cannot cancel an
          // accepted export, and its job ID is required to avoid starting an
          // overlapping export when the user retries.
          timeout: JIG_TRANSFER_TRIGGER_TIMEOUT_MS,
        },
        `starting export for Jig ${payload.hashId}`,
      )
      if (!start.success || !start.data) {
        const confirmed = Boolean(start.statusCode && start.statusCode >= 400 && start.statusCode < 500 && start.statusCode !== 408)
        return {
          success: false,
          error: confirmed ? start.error : 'The export may have started, but its job ID could not be confirmed. Avoid starting another export until the existing request is resolved.',
          statusCode: start.statusCode,
          stage: 'start',
          failureKind: confirmed ? 'confirmed' : 'unknown',
        }
      }

      jobId = jobIdFromResponse(start.data)
      if (jobId === null) return {
        success: false,
        error: 'The export may have started, but its job ID could not be confirmed. Avoid starting another export until the existing request is resolved.',
        stage: 'start',
        failureKind: 'unknown',
      }
    }

    const job = await pollJigTransferJob(context, {
      tenantId: payload.tenantId,
      jobId,
      pollIntervalMs: payload.pollIntervalMs,
      pollTimeoutMs: payload.pollTimeoutMs,
      signal: payload.signal,
    })
    if (!job.success || !job.data) {
      return {
        success: false,
        error: job.error,
        statusCode: job.statusCode,
        stage: job.stage,
        failureKind: job.failureKind,
        jobId,
      }
    }

    const downloadUrl = job.data.jobMeta?.exportDownloadUrl
    if (!downloadUrl) {
      return { success: false, error: 'The completed Jig export did not return a download URL.', stage: 'job', jobId }
    }

    return {
      success: true,
      jobId,
      data: {
        hashId: job.data.jobMeta?.exportJigHashId || payload.hashId,
        downloadUrl,
        expiresAt: job.data.jobMeta?.exportDownloadExpiresAt,
      },
    }
  },

  deleteJigById(context: any, payload: any): Promise<boolean> {
    if (!payload.jigId || !payload.jigFormatVersion) {
      return Promise.reject('Need a jigId and jigFormatVersion')
    }
    let url = AppConst.APIEndpoint.myJigsUrl.concat('/', payload.jigId)
    if (payload.jigFormatVersion >= 4) {
      // use v2 if the format version is > 4
      url = AppConst.APIEndpoint.myJigsV2Url.concat('/', payload.jigId)
    }
    const tenantId = this.getMyTenantID(context.rootState.app)
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.DELETE,
        url,
      },
      context,
      'deleteFromMyJigsById',
      `deleting jig id: ${payload.jigId}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  copyJigById(context: any, payload: any): Promise<boolean> {
    const urlParams = {
      action: 'copy',
      name: payload.newName,
    }
    // Response object we get back on a 201 of the copy endpoint
    // MeJigV2CopyResponse is the structure returned when you copy a V2 Jig.
    // type MeJigV2CopyResponse struct {
    //     Status      string `json:"status"`
    //     HashId      string `json:"id"`
    //     Name        string `json:"name"`
    //     DeeplinkURL string `json:"deeplinkURL,omitempty"`
    //     Visibility  string `json:"visibility"`
    // }
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: `${AppConst.APIEndpoint.meJigsV2Url}/${payload.jigId}`,
        params: urlParams,
      },
      context,
      'updateCopyJigResult',
      `copying jig ${payload.jigId} with new name ${payload.newName}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: payload.tenantId,
      }
    )
  },

  // Staff-only: copy a Jig into a chosen staff user's tenant for troubleshooting.
  // Backed by JIG-7099. Mirrors copyJigById but targets the staff copy endpoint.
  // copyOwnerUid defaults to the signed-in staff user's UID when omitted/blank.
  // Auth, app-name, tenant, and session-id headers are attached by apiRequest +
  // the global axios interceptor.
  copyJigToMe(
    context: any,
    payload: { jigId: string; name?: string; copyOwnerUid?: string }
  ): Promise<boolean> {
    const trimmedName = (payload.name || '').trim()
    const name = trimmedName.length > 0 ? trimmedName : `Copy of ${payload.jigId}`

    const trimmedUid = (payload.copyOwnerUid || '').trim()
    const copyOwnerUid = trimmedUid.length > 0 ? trimmedUid : this.authService().uid

    const urlParams = {
      'copy-owner-uid': copyOwnerUid,
      name,
    }

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: `${AppConst.APIEndpoint.jigsV2Url}/${payload.jigId}/copy-to-staff`,
        params: urlParams,
      },
      context,
      'updateCopyJigResult',
      `copying jig ${payload.jigId} to staff tenant`,
      StoreConst.StoreHelper.loadingMutationName,
      {},
      // A failed copy-to-staff is commonly an environment mismatch: the Jig ID
      // belongs to a different environment than the dashboard session. 404s get
      // a clean hard-coded hint because the server body can be an unreadable SQL
      // trace; other failures surface the server message when present.
      (errorContext: any, error: any) => {
        const status = error && error.response ? error.response.status : null
        const responseMessage = (error && error.response && error.response.data && error.response.data.message) || ''
        let failureMessage =
          "It may not exist in this environment — confirm you're signed into the same environment as the Jig (e.g. Prod EU vs Prod US), then try again."
        if (status === 404) {
          failureMessage =
            "The Jig was not found in this environment — confirm you're signed into the same environment as the Jig (e.g. Prod EU vs Prod US), then try again."
        } else if (responseMessage.length > 0) {
          failureMessage = responseMessage
        }
        const statusInfo = status != null ? ` (server returned ${status})` : ''
        // Record the failure details so the Command Centre inline result can show them.
        errorContext.commit('updateCopyJigResult', { id: '', name: '', failureStatus: status, failureMessage })
        errorContext.commit(
          StoreConst.StoreHelper.SetApiErrorMutationName,
          `Couldn't copy Jig ${payload.jigId}${statusInfo}. ${failureMessage}`,
          { root: true }
        )
        errorContext.commit(StoreConst.StoreHelper.loadingMutationName, false, { root: true })
        errorContext.commit(StoreConst.StoreHelper.loadingModalMutationName, false, { root: true })
      }
    )
  },

  async updateJigMetadata(context: any, payload: UpdateJigMetadataRequest): Promise<boolean> {
    const jigMetadataPayload = payload.jigMetadata

    if (!jigMetadataPayload || !jigMetadataPayload.Id || jigMetadataPayload.Version == null) {
      consoleError(
        `Invalid updateJigMetadata payload. Required payload type: JigMetadata
      Received payload:`,
        payload
      )
      return Promise.resolve(false)
    } else {
      const tenantId = this.getMyTenantID(context.rootState.app)

      const requestQueryParam = payload.queryParams ? { ...payload.queryParams } : {}
      const results: any = {}
      requestQueryParam.version = jigMetadataPayload.Version

      results.isSuccess = await apiRequest(
        {
          action: UtilsConst.HTTPAction.PUT,
          url: `${AppConst.APIEndpoint.meJigsV2Url}/${jigMetadataPayload.Id}/${AppConst.APIEndpoint.jigMetadataV1}`,
          body: jigMetadataPayload,
          params: requestQueryParam,
        },
        context,
        'updateSaveJigResult',
        `updating metadata of Jig ${jigMetadataPayload.Id}`,
        StoreConst.StoreHelper.loadingMutationName,
        {
          [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
        },
        (context: any, error: any, errorMsg: string) => {
          if (error.response) {
            results.status = error.response.status
          } else if (
            error.message.includes(`status code ${MueConst.nonSystemErrors.unprocessableEntity.status}`) ||
            error.message.includes('E040019')
          ) {
            results.status = MueConst.nonSystemErrors.unprocessableEntity.status
          }

          if (payload.defaultErrorHandleRequired) {
            consoleLog(`Updating metadata of Jig ${jigMetadataPayload.Id} failed, error: ${JSON.stringify(error.response)}`)

            context.commit(
              StoreConst.StoreHelper.SetApiErrorMutationName,
              `Error happened when ${errorMsg}. ${error.response.data.message}`,
              { root: true }
            )
          }
        }
      )

      return results
    }
  },

  async updateJigVisibility(context: any, payload: JigVisibilities): Promise<boolean> {
    if (!payload || !payload.visibility || !payload.version == null) {
      consoleError(
        `Invalid updateJigVisibility payload. Required payload:
        JigVisibilities {
          jigId: string
          visibility: string
          regenPassword: boolean
          version: number
        }
      Received payload:`,
        payload
      )
      return Promise.resolve(false)
    } else {
      const tenantId = this.getMyTenantID(context.rootState.app)
      const results: any = {}

      results.isSuccess = await apiRequest(
        {
          action: UtilsConst.HTTPAction.PUT,
          url: `${AppConst.APIEndpoint.myJigsV2Url}/${payload.jigId}/visibility/${payload.visibility}`,
          body: payload,
          params: {
            regenPassword: payload.regenPassword,
            version: payload.version,
          },
        },
        context,
        'updateSaveJigVisibilityResult',
        `updating jig ${payload.jigId} visibility`,
        StoreConst.StoreHelper.loadingMutationName,
        {
          [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
        },
        (context: any, error: any, errorMsg: string) => {
          if (error.response) {
            results.status = error.response.status
          } else if (
            error.message.includes(`status code ${MueConst.nonSystemErrors.unprocessableEntity.status}`) ||
            error.message.includes('E040019')
          ) {
            results.status = MueConst.nonSystemErrors.unprocessableEntity.status
          }
        }
      )

      return results
    }
  },

  loadJigPermissions(context: any, payload: any): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: `${AppConst.APIEndpoint.myJigsV2Url}/${payload.jigId}/permission`,
      },
      context,
      'updateJigPermissions',
      `getting jig permissions`,
      StoreConst.StoreHelper.loadingModalMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  createJigPermission(context: any, payload: JigPermissionCreateRequest): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: `${AppConst.APIEndpoint.myJigsV2Url}/${payload.JigId}/permission`,
        params: payload.UrlParams,
        // body: payload.Perm,
      },
      context,
      'updateJigPermissions',
      `creating jig permission`,
      StoreConst.StoreHelper.loadingModalMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  deleteJigPermission(context: any, payload: JigPermissionDeleteRequest): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.DELETE,
        url: `${AppConst.APIEndpoint.myJigsV2Url}/${payload.ResID}/permission/${payload.PermID}`,
      },
      context,
      '',
      `deleting jig permission`,
      StoreConst.StoreHelper.loadingModalMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  updateJigPermission(context: any, payload: JigPermissionUpdateRequest): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.PUT,
        url: `${AppConst.APIEndpoint.meJigsV2Url}/${payload.JigId}/permissions/v1/${payload.Perm.ID}`,
        body: payload.Perm,
      },
      context,
      '', // We don't do anything with the response body as we need to request all permissions again.
      `updating jig permission`,
      StoreConst.StoreHelper.loadingModalMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  clearJigShareLink(context: any, payload: JigShareURLData) {
    payload.requestInFlight = false
    payload.shareURL = ''
    context.commit('setRequestingJigShareLink', payload)
  },

  async changeJigOwner(
    context: any,
    payload: ChangeJigOwnerRequest
  ): Promise<{
    isSuccessful: boolean
    terminate: boolean
  }> {
    if (payload.JigId.length === 0 || payload.DesiredOwnerEmail.length === 0) {
      consoleError(
        `Invalid changeJigOwner payload. Required payload:
        ChangeJigOwnerRequest {
          JigId: string
          DesiredOwnerEmail: string
          DesiredOwnerTenantID?: number
        }
      Received payload:`,
        payload
      )
      context.commit(StoreConst.StoreHelper.SetApiErrorMutationName, `Invalid changeJigOwner payload received.`, {
        root: true,
      })
      return new Promise(() => ({
        isSuccessful: false,
        terminate: false,
      }))
    } else {
      const urlParams: any = {
        email: payload.DesiredOwnerEmail,
      }
      if (payload.action) {
        urlParams.action = payload.action
      }
      if (payload.DesiredOwnerTenantID) {
        urlParams.tenantID = payload.DesiredOwnerTenantID
      }

      const tenantId = this.getMyTenantID(context.rootState.app)
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${this.authService().accessToken}`,
          [UtilsConst.RequestHeaders.CustomHeaderKeyAppName]: AppConst.appName,
          [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
        },
        params: urlParams,
      }

      try {
        const response: any = await Axios.put(`${AppConst.apiDomain}${AppConst.APIEndpoint.myJigsV2Url}/${payload.JigId}/owner`, {}, config)

        if (response.data) {
          context.commit('updateChangeJigOwnerResult', response.data as ChangeJigOwnerResult)

          return {
            isSuccessful: response.data.status === 'success',
            terminate: response.data.message !== `Resource not found: ${payload.JigId}`,
          }
        } else {
          consoleLog(`Jig ownership change failed, response was: ${JSON.stringify(response)}`)
          context.commit(StoreConst.StoreHelper.SetApiErrorMutationName, `Something went wrong when changing Jig ownership`, { root: true })
          return {
            isSuccessful: false,
            terminate: false,
          }
        }
      } catch (error: any) {
        consoleLog(`Jig ownership change failed, error: ${JSON.stringify(error.response)}`)
        const terminateAllRequests = error.response.data.message !== `Resource not found: ${payload.JigId}`

        if (terminateAllRequests) {
          context.commit(
            StoreConst.StoreHelper.SetApiErrorMutationName,
            `Error happened when changing Jig ownership. ${error.response.data.message}`,
            { root: true }
          )
        }

        return {
          isSuccessful: false,
          terminate: terminateAllRequests,
        }
      }
    }
  },

  changeJigOwnerTenant(context: any, payload: ChangeOwnerTenantRequest): Promise<boolean> {
    const msg = `changing jig owner. Jig ID: to ${payload.JigId}, tenant ID: ${payload.DesiredOwnerTenantID}`
    if (payload.JigId.length === 0 || payload.DesiredOwnerTenantID === TenantHelpers.InvalidTenantID) {
      consoleError(
        `Invalid changeJigOwnerTenant payload. Required payload:
        ChangeOwnerTenantRequest {
          JigId: string
          DesiredOwnerTenantID: number
        }
      Received payload:`,
        payload
      )
      return Promise.resolve(false)
    } else {
      const urlParams = {
        tenantID: payload.DesiredOwnerTenantID,
      }
      return apiRequest(
        {
          action: UtilsConst.HTTPAction.PUT,
          url: `${AppConst.APIEndpoint.myJigsUrl}/v2/${payload.JigId}/owner`,
          params: urlParams,
        },
        context,
        'updateChangeJigOwnerResult',
        msg
      )
    }
  },

  async requestJigDeeplinkURL(context: any, payload: JigShareRequest): Promise<boolean> {
    if (payload == null) {
      return new Promise((reject) => reject(false))
    }

    return requestBranchJigLinkViaHttpApi(context, payload.jig, payload.branchIOCustomData, payload.targetApp)
  },

  async repairJigDeeplink(context: any, payload: { jigId: string }): Promise<RepairDeeplinkResult> {
    const jigId = (payload && payload.jigId ? payload.jigId : '').trim()
    if (jigId.length === 0) {
      return { status: 400 }
    }

    try {
      const tenantId = this.getMyTenantID(context.rootState.app)
      const config = buildAuthConfig(this.authService().accessToken, tenantId)
      const url = `${AppConst.apiDomain}${AppConst.APIEndpoint.jigsV1Url}/${jigId}/deeplinks/v1/repair`

      const response: AxiosResponse<any> = await Axios.put(url, {}, config)
      return {
        status: response.status,
        outcome: response.data ? response.data.outcome : undefined,
        deeplinkUrl: response.data ? response.data.deeplink_url : undefined,
      }
    } catch (error: any) {
      const response = error.response
      if (!response) {
        // Setup failure (missing auth/tenant) or network/transport error — no HTTP status available.
        return { status: 0 }
      }

      const result: RepairDeeplinkResult = { status: response.status }

      if (response.status === 423 && response.data && response.data.locked_by) {
        result.lockedBy = {
          name: response.data.locked_by.name,
          email: response.data.locked_by.email,
        }
      }

      if (response.status === 429) {
        const retryAfter = response.headers && response.headers['retry-after']
        const parsed = retryAfter != null ? parseInt(retryAfter, 10) : NaN
        result.retryAfter = isNaN(parsed) ? undefined : parsed
      }

      return result
    }
  },

  async uploadJigThumbnail(context: any, payload: UploadThumbnailRequest): Promise<boolean> {
    if (payload == null || payload.Id.length === 0 || !payload.TenantId || payload.Thumbnail.size === 0 || payload.version == null) {
      consoleError(
        `Invalid uploadJigThumbnail payload. Required payload:
        UploadThumbnailRequest {
          Id: string
          TenantId: number
          Thumbnail: File
          IsFirstThumbnail: boolean
          version?: number
          DateUpdated?: string
        }
      Received payload:`,
        payload
      )
      return Promise.resolve(false)
    } else {
      const file = new Blob([payload.Thumbnail], {
        type: 'application/octet-stream',
      })
      const formData = new FormData()
      // We change the thumbnail name before we send it as form data
      formData.append('image', file, `jig-${payload.Id}-color`)
      const urlParams = {
        cl: '6x6', // Compression Level Block Size (copied from Unity client)
        mode: 'medium', // Compression Speed (copied from Unity client)
        version: payload.version, // Jig's current version value in JigMetadata
      }
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${this.authService().accessToken}`,
          [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: payload.TenantId,
        },
        params: urlParams,
        timeout: 300 * 1000, // 5 minutes
        baseURL: `${AppConst.apiDomain}`,
      }
      const request = {
        action: payload.IsFirstThumbnail ? UtilsConst.HTTPAction.POST : UtilsConst.HTTPAction.PUT,
        url: `${AppConst.APIEndpoint.myJigsV2Url}/${payload.Id}/thumbnail`,
        body: formData,
      }
      const requestResults: any = {}
      requestResults.isSuccess = await apiRequestCustomConfig(
        request,
        context,
        'uploadThumbnailResult',
        `uploading new Jig thumbnail. Jig ID: ${payload.Id}, file: ${payload.Thumbnail.name}`,
        config,
        undefined,
        (context: any, error: any, errorMsg: string, onApiCatchError: any) => {
          if (error.response) {
            requestResults.status = error.response.status
            // When SUP error happens, we use a custom error message in UI, all other errors should use default API error catch event.
            if (
              requestResults.status !== MueConst.nonSystemErrors.unprocessableEntity.status &&
              requestResults.status !== MueConst.nonSystemErrors.conflict.status
            ) {
              onApiCatchError(context, error, errorMsg)
            }
          } else if (
            error.message.includes(`status code ${MueConst.nonSystemErrors.unprocessableEntity.status}`) ||
            error.message.includes('E040019')
          ) {
            requestResults.status = MueConst.nonSystemErrors.unprocessableEntity.status
          } else if (error.message.includes(`status code ${MueConst.nonSystemErrors.conflict.status}`)) {
            requestResults.status = MueConst.nonSystemErrors.conflict.status
          }
        }
      )

      return requestResults
    }
  },

  // create lock POST
  async createLock(context: any, payload: LockRequestPayload) {
    const mockedActivity = getJigSpaceServiceLockMock(payload.jigId, context.state?.jigMetadata?.UserActivity)

    if (mockedActivity != null) {
      context.commit('updateJigMetadataUserActivity', mockedActivity)
      return {
        isSuccessful: false,
        status: MueConst.LockResponseCode.lockAlreadyExists,
        message: 'Lock already exists.',
      }
    }

    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${this.authService().accessToken}`,
        [UtilsConst.RequestHeaders.CustomHeaderKeyAppName]: AppConst.appName,
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: payload.tenantId,
      },
      params: {
        action: MueConst.LockAction.createLock,
      },
      baseURL: `${AppConst.apiDomain}`,
    }

    try {
      const response: any = await Axios.post(`${AppConst.APIEndpoint.meJigsV2Url}/${payload.jigId}`, {}, config)

      if (response.status === MueConst.LockResponseCode.lockObtained || response.status === MueConst.LockResponseCode.lockUpdated) {
        return {
          isSuccessful: true,
          status: response.status,
          message: 'Lock obtained.',
        }
      } else if (response.status === MueConst.LockResponseCode.lockAlreadyExists) {
        context.commit('updateJigMetadataUserActivity', response.data.UserActivity)
        return {
          isSuccessful: false,
          status: response.status,
          message: 'Lock already exists.',
        }
      } else {
        consoleLog(`Jig lock creation failed, response was: ${JSON.stringify(response)}`)
        context.commit(StoreConst.StoreHelper.SetApiErrorMutationName, `Something went wrong when creating a Jig lock`, {
          root: true,
        })
        return {
          isSuccessful: false,
          status: response.status,
          message: 'Something went wrong when creating a Jig lock',
        }
      }
    } catch (error: any) {
      consoleLog(`Jig lock creation failed, error: ${error.response ? JSON.stringify(error.response) : JSON.stringify(error)}`)
      let errorMessage = 'Something went wrong when creating a Jig lock'

      if (error.response && error.response.status === MueConst.LockResponseCode.lockAlreadyExists) {
        context.commit('updateJigMetadataUserActivity', error.response.data.UserActivity)
        errorMessage = 'Lock already exists.'
      } else if (error.name === 'AxiosError') {
        context.commit(
          StoreConst.StoreHelper.SetApiErrorMutationName,
          `Axios error happened when trying to create a Jig lock. ${error.message}`,
          { root: true }
        )
      } else {
        context.commit(
          StoreConst.StoreHelper.SetApiErrorMutationName,
          `Error happened when trying to create a Jig lock. ${error.response.data.message || ''}`,
          { root: true }
        )
      }

      return {
        isSuccessful: false,
        status: error.response ? error.response.status : error.status,
        message: errorMessage,
      }
    }
  },

  // release lock
  async releaseLock(context: any, payload: LockRequestPayload) {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${this.authService().accessToken}`,
        [UtilsConst.RequestHeaders.CustomHeaderKeyAppName]: AppConst.appName,
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: payload.tenantId,
      },
      params: {
        action: MueConst.LockAction.releaseLock,
      },
      baseURL: `${AppConst.apiDomain}`,
    }

    try {
      const response: any = await Axios.put(`${AppConst.APIEndpoint.meJigsV2Url}/${payload.jigId}`, {}, config)

      if (response.status === MueConst.LockResponseCode.lockRemoved) {
        context.commit('updateJigMetadataUserActivity', response.data.UserActivity)
        return {
          isSuccessful: true,
          status: response.status,
          message: 'Lock removed.',
        }
      } else {
        consoleLog(`Jig lock release failed, response was: ${JSON.stringify(response)}`)
        context.commit(StoreConst.StoreHelper.SetApiErrorMutationName, `Something went wrong when releasing a Jig lock`, {
          root: true,
        })
        return {
          isSuccessful: false,
          status: response.status,
          message: 'Something went wrong when releasing a Jig lock',
        }
      }
    } catch (error: any) {
      consoleLog(`Jig lock release failed, error: ${error.response ? JSON.stringify(error.response) : JSON.stringify(error)}`)
      context.commit(
        StoreConst.StoreHelper.SetApiErrorMutationName,
        `Error happened when trying to release a Jig lock. ${error.response ? error.response.data.message : error.message}`,
        { root: true }
      )
      return {
        isSuccessful: false,
        status: error.response ? error.response.status : error.status,
        message: 'Something went wrong when releasing a Jig lock',
      }
    }
  },

  // notify awaiting lock
  async notifyAwaitingLock(context: any, payload: LockRequestPayload) {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${this.authService().accessToken}`,
        [UtilsConst.RequestHeaders.CustomHeaderKeyAppName]: AppConst.appName,
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: payload.tenantId,
      },
      params: {
        action: MueConst.LockAction.notifyAwaitingLock,
        medium: 'email',
      },
      baseURL: `${AppConst.apiDomain}`,
    }

    try {
      const response: any = await Axios.put(`${AppConst.APIEndpoint.meJigsV2Url}/${payload.jigId}`, {}, config)

      if (response.status === MueConst.LockResponseCode.emailSent) {
        return {
          isSuccessful: true,
          status: response.status,
          message: response.data.message,
          sendStatus: response.data.status,
        }
      } else {
        consoleLog(`Notifying awaiting Jig lock email send failed, response was: ${JSON.stringify(response)}`)
        context.commit(StoreConst.StoreHelper.SetApiErrorMutationName, `Something went wrong when sending awaiting Jig lock email`, {
          root: true,
        })
        return {
          isSuccessful: false,
          status: response.status,
          message: 'Something went wrong when sending awaiting Jig lock email',
          sendStatus: MueConst.MUENotifyEmailStatus.failed,
        }
      }
    } catch (error: any) {
      consoleLog(`Notifying awaiting Jig lock email send failed, error: ${JSON.stringify(error.response)}`)

      if (error.response.status !== MueConst.MUENotifyEmailStatus.invalidJigIDCode) {
        context.commit(
          StoreConst.StoreHelper.SetApiErrorMutationName,
          `Error happened when trying to send awaiting Jig lock email. ${error.response.data.message}`,
          { root: true }
        )
      }

      return {
        isSuccessful: false,
        status: error.response.status,
        message: 'Something went wrong when sending awaiting Jig lock email',
        sendStatus:
          error.response.status === MueConst.MUENotifyEmailStatus.invalidJigIDCode
            ? MueConst.MUENotifyEmailStatus.invalidJigID
            : MueConst.MUENotifyEmailStatus.failed,
      }
    }
  },

  // break an editing lock - staff superuser only
  async breakLock(context: any, payload: BreakLockRequestPayload) {
    const tenantId = this.getMyTenantID(context.rootState.app)
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${this.authService().accessToken}`,
        [UtilsConst.RequestHeaders.CustomHeaderKeyAppName]: AppConst.appName,
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      },
      params: {
        action: MueConst.LockAction.breakLock,
      },
      baseURL: `${AppConst.apiDomain}`,
    }

    try {
      const response: any = await Axios.put(`${AppConst.APIEndpoint.jigsV2Url}/${payload.jigId}`, {}, config)

      if (response && response.status === MueConst.LockResponseCode.lockRemoved) {
        return {
          isSuccessful: true,
          status: response.status,
          message: 'Lock removed.',
        }
      } else {
        consoleLog(`Jig lock break failed, response was: ${JSON.stringify(response)}`)
        context.commit(StoreConst.StoreHelper.SetApiErrorMutationName, `Something went wrong when breaking a Jig lock`, {
          root: true,
        })
        return {
          isSuccessful: false,
          status: response ? response.status : -1,
          message: 'Something went wrong when breaking a Jig lock.',
        }
      }
    } catch (error: any) {
      consoleLog(`Jig lock break failed, error: ${JSON.stringify(error.response)}`)

      context.commit(
        StoreConst.StoreHelper.SetApiErrorMutationName,
        `Error happened when trying to break a Jig lock. ${error.response ? error.response.data.message : ''}`,
        { root: true }
      )

      return {
        isSuccessful: false,
        status: error.response ? error.response.status : -1,
        message: 'Something went wrong when breaking a Jig lock.',
      }
    }
  },
}

function buildAuthConfig(accessToken: string, tenantId: number): AxiosRequestConfig {
  return {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      [UtilsConst.RequestHeaders.CustomHeaderKeyAppName]: AppConst.appName,
      [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
    },
  }
}

async function requestBranchJigLinkViaHttpApi(context: any, jig: JigMetadata, requestData: any, targetApp: string): Promise<boolean> {
  if (context === null) {
    return new Promise((reject) => reject(false))
  }

  const jigDeeplinkURLRequesting = jig.DeeplinkURLRequesting

  // Can't request the same share link at the same time.
  if (jigDeeplinkURLRequesting !== undefined && jigDeeplinkURLRequesting) {
    return new Promise((reject) => reject(false))
  }

  const deeplink = jig.DeeplinkURL

  // We've already got a share url, return success
  if (deeplink !== undefined && deeplink.length > 0) {
    return new Promise((resolve) => resolve(true))
  }

  const jigID = jig.Id
  const tenantId = this.getMyTenantID(context.rootState.app)

  context.commit('setRequestingJigShareLink', {
    jigId: jigID,
    requestInFlight: true,
    shareURL: '',
  } as JigShareURLData)

  const config: AxiosRequestConfig = {
    headers: {
      Authorization: `Bearer ${this.authService().accessToken}`,
      [UtilsConst.RequestHeaders.CustomHeaderKeyAppName]: AppConst.appName,
      [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
    },
  }

  const reqBody = {
    ...requestData,
  }
  let response!: AxiosResponse<any>

  const url = `${AppConst.apiDomain}${AppConst.APIEndpoint.jigsV1Url}/${jigID}/deeplinks/v1?target_app=${targetApp}`
  response = await Axios.post(url, reqBody, config)
  if (response.data && response.data.url !== undefined && response.data.url.length > 0) {
    consoleLog(`Jig Share URL: ${response.data.url}`)
    context.commit('setRequestingJigShareLink', {
      jigId: jigID,
      requestInFlight: false,
      shareURL: response.data.url,
    } as JigShareURLData)
    return true
  } else {
    consoleLog(`Jig Share URL was not successful, response was: ${JSON.stringify(response)}`)
    context.commit('setRequestingJigShareLink', {
      jigId: jigID,
      requestInFlight: false,
      shareURL: '',
    } as JigShareURLData)
    context.commit(StoreConst.StoreHelper.SetApiErrorMutationName, `Something went wrong getting a link for this Jig.`, {
      root: true,
    })
    return false
  }
}
